import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Col, Container, Row, Card } from "react-bootstrap";
import { AuthenticationService } from "../Services/AuthService";
import { Button, Modal, Input, Form } from "antd";
import { encrypt } from "../global/EncryptionASE";
import { UserService } from "../Services/UserService";
import Loader from "../global/Loader";
import Swal from "sweetalert2";
import loadingImg from "../assets/images/loading-img.gif";

const ChangePassword = () => {
    const authService = useMemo(() => new AuthenticationService(), []);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loadingModalSubmit, setLoadingModalSubmit] = useState(false);
    const userService = useMemo(() => new UserService(), []);
    const [tblData, setTblData] = useState([]);
    const [loadingTblData, setLoadingTblData] = useState(false);

    // ✅ fixed: call method if it's a function
    const userId = authService.getUserData?.userId ?? authService.getUserData()?.userId;

    // ✅ useCallback to fix ESLint warning
    const getImageById = useCallback(
        async (imgDocUniqueId, imgDocType = "image/jpeg") => {
            try {
                if (imgDocUniqueId !== null) {
                    const res = await userService.ViewDocument(imgDocUniqueId);
                    if (res.status === 200) {
                        const fileBlob = new Blob([res.data], { type: imgDocType });
                        return URL.createObjectURL(fileBlob);
                    }
                }
            } catch (error) {
                console.error("Error fetching image:", error);
            }
            return loadingImg;
        },
        [userService] // ✅ dependency
    );

    // Fetch user details
    useEffect(() => {
        const getUserDetailsById = async (userId) => {
            try {
                setLoadingTblData(true);
                const res = await userService.GetUserDetailsById(userId);

                let data = res.data.table.map((item, index) => ({
                    key: index + 1,
                    userId: item.userId,
                    emailId: item.emailId,
                    oldPassword: item.oldPassword,
                    userRole: item.userRole,
                    userName: item.userName,
                    officerImgId: item.officerImgId,
                    ImgUrl: loadingImg,
                }));

                // ✅ fix: Promise.all instead of async forEach
                const updatedData = await Promise.all(
                    data.map(async (item) => {
                        const actualImgUrl = await getImageById(item.officerImgId);
                        return { ...item, ImgUrl: actualImgUrl };
                    })
                );

                setTblData(updatedData);
            } catch (error) {
                console.error("Error fetching GetUserDetailsById:", error);
            } finally {
                setLoadingTblData(false);
            }
        };

        if (userId) {
            getUserDetailsById(userId);
        }
    }, [userService, userId, getImageById]); // ✅ added getImageById

    // Handle modal submit
    const modalSubmit = async () => {
        try {
            setLoadingModalSubmit(true);

            const oldPasswordInput = form.getFieldValue("oldPassword");
            const newPasswordInput = form.getFieldValue("newPassword");

            if (tblData.length === 0) return;

            // ✅ fix: encrypt both sides consistently
            if (encrypt(oldPasswordInput) === tblData[0].oldPassword) {
                if (encrypt(newPasswordInput) !== tblData[0].oldPassword) {
                    let payload = {
                        userId: authService.getUserData?.userId ?? authService.getUserData()?.userId,
                        newPassword: encrypt(newPasswordInput),
                    };
                   const res = await userService.ChageUserPassword(payload);

                    if (res.data.status) {
                        Swal.fire({
                            html: res.data.message,
                            icon: "success",
                            showConfirmButton: false,
                            timer: 2000,
                        });
                        authService.logout();
                    } else {
                        Swal.fire({
                            icon: "error",
                            title: "Oops...",
                            text: res.data.message,
                        });
                    }
                    modalClose();
                } else {
                    Swal.fire({
                        icon: "error",
                        title: "Oops...",
                        text: "New password must be different. Try again..",
                    });
                    form.resetFields();
                }
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: "Old password does not match. Try again..",
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingModalSubmit(false);
        }
    };

    const showModal = () => {
        form.resetFields();
        setIsModalVisible(true);
    };

    const modalClose = () => {
        form.resetFields();
        setIsModalVisible(false);
    };

    return (
        <>
            {loadingTblData && <Loader />}
            <Container>
                <Row>
                    <Col className="dashboard">
                        <Card>
                            <Card.Header>
                                <h3>Change Password</h3>
                            </Card.Header>
                            <div className="password-change mb-5">
                                <div className="wrapper">
                                    <div className="user-card">
                                        <div className="user-card-img">
                                            <img
                                                src={tblData.length > 0 ? tblData[0].ImgUrl : loadingImg}
                                                alt="User"
                                            />
                                        </div>
                                        {tblData.length > 0 && (
                                            <div className="user-card-info">
                                                <h2>{tblData[0].userName}</h2>
                                                <p><span>Email:</span> {tblData[0].emailId}</p>
                                                <p><span>User S#:</span>{tblData[0].userId}</p>
                                                <p><span>User Role:</span> {tblData[0].userRole}</p>
                                                <Button
                                                    className="btn btn-sm btn-outline-primary mt-3"
                                                    onClick={showModal}
                                                >
                                                    Change Password
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Modal for changing password */}
                        <Modal
                            title="Change Password"
                            style={{ top: 20 }}
                            open={isModalVisible}
                            okButtonProps={{ hidden: true }}
                            cancelButtonProps={{ hidden: true }}
                            onCancel={modalClose}
                        >
                            <Form
                                layout="vertical"
                                form={form}
                                name="passwordChangeForm"
                                onFinish={modalSubmit}
                                scrollToFirstError
                            >
                                <Card>
                                    <Card.Body>
                                        <Col md={12}>
                                            <Form.Item
                                                label="Old Password"
                                                name="oldPassword"
                                                rules={[{ required: true, message: "Please input your old password!" }]}
                                            >
                                                <Input.Password autoComplete="off" />
                                            </Form.Item>
                                        </Col>

                                        <Col md={12}>
                                            <Form.Item
                                                label="New Password"
                                                name="newPassword"
                                                rules={[
                                                    { required: true, message: "Please input your new password!" },
                                                    { min: 8, max: 72, message: "Password must be between 8 and 72 characters long." },
                                                    {
                                                        pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
                                                        message: "Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.",
                                                    },
                                                    {
                                                        pattern: /^(?!.*(012|123|234|345|456|567|678|789|987|876|765|654|543|432|321|0123|1234|2345|3456|4567|5678|6789|9876|8765|7654|6543|5432|4321)).*$/,
                                                        message: "Password cannot contain continuous number sequences like 123, 456, 9876, etc.",
                                                    },
                                                    {
                                                        validator: (_, value) => {
                                                            if (!value) return Promise.resolve();

                                                            const commonPasswords = [
                                                                "password", "password123", "12345678", "qwerty", "abc123",
                                                                "ccost@123", "cgcost@123", "user@123", "nodal@123",
                                                            ];
                                                            if (commonPasswords.includes(value.toLowerCase())) {
                                                                return Promise.reject(
                                                                    new Error("This password is too common. Please choose a stronger one.")
                                                                );
                                                            }
                                                            if (
                                                                value.toLowerCase().includes("ccost") ||
                                                                value.toLowerCase().includes("cost") ||
                                                                value.toLowerCase().includes("cgcost") ||
                                                                value.toLowerCase().includes("admin") ||
                                                                value.toLowerCase().includes("user") ||
                                                                value.toLowerCase().includes("nodal") ||
                                                                value.toLowerCase().includes("raipur")
                                                            ) {
                                                                return Promise.reject(
                                                                    new Error("Password cannot contain application-specific words, username or app name.")
                                                                );
                                                            }

                                                            return Promise.resolve();
                                                        },
                                                    },
                                                ]}
                                            >
                                                <Input.Password autoComplete="off" />
                                            </Form.Item>
                                        </Col>

                                        <Col md={12}>
                                            <Form.Item
                                                label="Confirm Password"
                                                name="confirmPassword"
                                                dependencies={["newPassword"]}
                                                rules={[
                                                    { required: true, message: "Please confirm your new password!" },
                                                    ({ getFieldValue }) => ({
                                                        validator(_, value) {
                                                            if (!value || getFieldValue("newPassword") === value) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(
                                                                new Error("New Password & Confirm Password do not match!")
                                                            );
                                                        },
                                                    }),
                                                ]}
                                            >
                                                <Input.Password autoComplete="off" />
                                            </Form.Item>
                                        </Col>

                                        <Col md={12}>
                                            <hr />
                                        </Col>

                                        <Col md={12} className="text-center">
                                            <button
                                                type="submit"
                                                className="btn btn-sm btn-primary me-2"
                                                disabled={loadingModalSubmit}
                                            >
                                                Submit
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                disabled={loadingModalSubmit}
                                                onClick={modalClose}
                                            >
                                                Cancel
                                            </button>
                                        </Col>
                                    </Card.Body>
                                </Card>
                            </Form>
                        </Modal>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default ChangePassword;




// import React, { useEffect, useState, useMemo } from "react";
// import { Col, Container, Row, Card } from "react-bootstrap";
// import { AuthenticationService } from "../Services/AuthService";
// import { Button, Modal, Input, Form } from "antd";
// import { set } from "../global/Encryption";
// import { UserService } from "../Services/UserService";
// import Loader from "../global/Loader";
// import Swal from "sweetalert2";
// // import DemoUser from "../assets/images/demo-user.jpg";
// import loadingImg from "../assets/images/loading-img.gif";

// const ChangePassword = () => {
//     const authService = useMemo(() => new AuthenticationService(), []);
//     const [form] = Form.useForm();
//     const [isModalVisible, setIsModalVisible] = useState(false);
//     const [loadingModalSubmit, setLoadingModalSubmit] = useState(false);
//     const userService = useMemo(() => new UserService(), []);
//     const [tblData, setTblData] = useState([]);
//     const [loadingTblData, setLoadingTblData] = useState(false);
//     const userId = authService.getUserData?.userId;


    

//     useEffect(() => {
//         const getImageById = async (imgDocUniqueId, imgDocType) => {
//         try {
//             if (imgDocUniqueId !== null) {
//             const res = await userService.ViewDocument(imgDocUniqueId);
//             if (res.status === 200) {
//                 const fileBlob = new Blob([res.data], { type: imgDocType });
//                 const dataUrl = URL.createObjectURL(fileBlob);
//                 return dataUrl;
//             }
//             }
//         } catch (error) {
//             console.error("Error fetching image:", error);
//         }
//         return loadingImg;
//         };

//         const getUserDetailsById = async (userId) => {
//             try {
//                 setLoadingTblData(true);
//                 const res = await userService.GetUserDetailsById(userId);
//                 let data = res.data.table.map((item, index) => ({
//                     key: index + 1,
//                     userId: item.userId,
//                     emailId: item.emailId,
//                     oldPassword: item.oldPassword,
//                     userRole: item.userRole,
//                     userName: item.userName,
//                     officerImgId:item.officerImgId,
//                     ImgUrl: loadingImg,
//                 }));
//                 setTblData(data);

//                 data.forEach(async (item, index) => {
//           const actualImgUrl = await getImageById(item.officerImgId, "");
//           setTblData((prevData) => {
//             const updatedData = [...prevData];
//             updatedData[index] = { ...updatedData[index], ImgUrl: actualImgUrl };
//             return updatedData;
//           });
//         });
//             } catch (error) {
//                 console.error("Error fetching GetUserDetailsById:", error);
//             } finally {
//                 setLoadingTblData(false);
//             }
//         };

//         if (userId) {
//             getUserDetailsById(userId);
//         }
//     }, [userService, userId]);

//     const modalSubmit = async () => {
//         try {
//             setLoadingModalSubmit(true);

//             if (tblData[0].oldPassword === set(form.getFieldValue('oldPassword'))) {

//                 if (tblData[0].oldPassword !== set(form.getFieldValue('newPassword'))) {

//                     let payload = {
//                         userId: authService.getUserData?.userId,
//                         newPassword: set(form.getFieldValue('newPassword'))
//                     };
//                     console.log(payload);
//                     const res = await userService.ChageUserPassword(payload);
//                     if (res.data.status) {
//                         Swal.fire({
//                             html: res.data.message,
//                             icon: "success",
//                             showConfirmButton: false,
//                             timer: 2000
//                         });
//                         authService.logout();

//                     } else {
//                         Swal.fire({
//                             icon: "error",
//                             title: "Oops...",
//                             text: res.data.message
//                         });
//                     }
//                     modalClose();
//                 }
//                 else {
//                     Swal.fire({
//                         icon: "error",
//                         title: "Oops...",
//                         text: 'New password must be different. Try again..'
//                     });
//                     form.resetFields();
//                 }




//             } else {
//                 Swal.fire({
//                     icon: "error",
//                     title: "Oops...",
//                     text: 'Old password does not match. Try again..'
//                 });
//             }


//         } catch (error) {
//             console.error(error);
//             setLoadingModalSubmit(false);
//         } finally {
//             setLoadingModalSubmit(false);
//         }
//     };

//     const showModal = () => {
//         form.resetFields();
//         setIsModalVisible(true);
//     };

//     const modalClose = () => {
//         form.resetFields();
//         setIsModalVisible(false);
//     };

//     return (
//         <>
//             {loadingTblData && <Loader />}
//             <Container>
//                 <Row>
//                     <Col className="dashboard">
//                         <Card className="">
//                             <Card.Header>
//                                 <h3>Change Password</h3>
//                             </Card.Header>
//                             <div className="password-change mb-5">
//                                 <div className="wrapper">
//                                     <div className="user-card">
//                                         <div className="user-card-img">
//                                             <img src={tblData[0].ImgUrl} alt="User" />
//                                         </div>
//                                         {tblData.length > 0 ? (
//                                             <div className="user-card-info">
//                                                 <h2>{tblData[0].userName}</h2>
//                                                 <p><span>Email:</span> {tblData[0].emailId}</p>
//                                                 <p><span>User S#:</span>{tblData[0].userId}</p>
//                                                 <p><span>User Role:</span> {tblData[0].userRole}</p>
//                                                 <Button className='btn btn-sm btn-outline-primary mt-3' onClick={showModal}>
//                                                     Change Password
//                                                 </Button>
//                                             </div>
//                                         ) : ""}
//                                     </div>
//                                 </div>
//                             </div>
//                         </Card>


//                         {/* <Modal
//                             title="Change Password"
//                             style={{ top: 20 }}
//                             open={isModalVisible}
//                             okButtonProps={{ hidden: true }}
//                             cancelButtonProps={{ hidden: true }}
//                             onCancel={() => setIsModalVisible(false)}
//                         >
//                             <Form
//                                 layout="vertical"
//                                 form={form}
//                                 name="passwordChangeForm"
//                                 onFinish={modalSubmit}
//                                 scrollToFirstError
//                             >

//                                 <Card>
//                                     <Card.Body>
//                                         <Col md={12}>
//                                             <Form.Item
//                                                 label="Old Password"
//                                                 name="oldPassword"
//                                                 rules={[{ required: true, message: 'Please input your old password!' }]}
//                                             >
//                                                 <Input.Password autoComplete="off" />
//                                             </Form.Item>
//                                         </Col>
//                                         <Col md={12}>
//                                             <Form.Item
//                                                 label="New Password"
//                                                 name="newPassword"
//                                                 rules={[
//                                                     { required: true, message: 'Please input your new password!' },
//                                                     {
//                                                         pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
//                                                         message: 'Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character.',
//                                                     },
//                                                 ]}
//                                             >
//                                                 <Input.Password autoComplete="off" />
//                                             </Form.Item>
//                                         </Col>
//                                         <Col md={12}>
//                                             <Form.Item
//                                                 label="Confirm Password"
//                                                 name="confirmPassword"
//                                                 dependencies={['newPassword']}
//                                                 rules={[
//                                                     { required: true, message: 'Please confirm your new password!' },
//                                                     ({ getFieldValue }) => ({
//                                                         validator(_, value) {
//                                                             if (!value || getFieldValue('newPassword') === value) {
//                                                                 return Promise.resolve();
//                                                             }
//                                                             return Promise.reject(new Error('New Password & Confirm Password do not match!'));
//                                                         },
//                                                     }),
//                                                 ]}
//                                             >
//                                                 <Input.Password autoComplete="off" />
//                                             </Form.Item>
//                                         </Col>
//                                         <Col md={12}>
//                                             <hr />
//                                         </Col>
//                                         <Col md={12} className="text-center">
//                                             <button
//                                                 type="submit"
//                                                 className="btn btn-sm btn-primary me-2"
//                                                 disabled={loadingModalSubmit}
//                                             >
//                                                 Submit
//                                             </button>
//                                             <button
//                                                 className="btn btn-sm btn-outline-danger"
//                                                 disabled={loadingModalSubmit}
//                                                 onClick={modalClose}
//                                             >
//                                                 Cancel
//                                             </button>
//                                         </Col>
//                                     </Card.Body>
//                                 </Card>
//                             </Form>
//                         </Modal> */}

//                         <Modal
//     title="Change Password"
//     style={{ top: 20 }}
//     open={isModalVisible}
//     okButtonProps={{ hidden: true }}
//     cancelButtonProps={{ hidden: true }}
//     onCancel={() => setIsModalVisible(false)}
// >
//     <Form
//         layout="vertical"
//         form={form}
//         name="passwordChangeForm"
//         onFinish={modalSubmit}
//         scrollToFirstError
//     >
//         <Card>
//             <Card.Body>
//                 <Col md={12}>
//                     <Form.Item
//                         label="Old Password"
//                         name="oldPassword"
//                         rules={[{ required: true, message: 'Please input your old password!' }]}
//                     >
//                         <Input.Password autoComplete="off" />
//                     </Form.Item>
//                 </Col>

//                 <Col md={12}>
//                     <Form.Item
//                         label="New Password"
//                         name="newPassword"
//                         rules={[
//                             { required: true, message: 'Please input your new password!' },
//                             {
//                                 min: 8,
//                                 max: 72,
//                                 message: 'Password must be between 8 and 72 characters long.',
//                             },
//                             {
//                                 pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
//                                 message: 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.',
//                             },
//                             {
//                                 validator: (_, value) => {
//                                     if (!value) return Promise.resolve();

//                                     // List of weak/common passwords
//                                     const commonPasswords = [
//                                         'password',
//                                         'password123',
//                                         '12345678',
//                                         'qwerty',
//                                         'abc123',
//                                         'Ccost@123',
//                                         'Cgcost@123',
//                                         'user@123',
//                                         'nodal@123',
//                                     ];
//                                     if (commonPasswords.includes(value.toLowerCase())) {
//                                         return Promise.reject(
//                                             new Error('This password is too common. Please choose a stronger one.')
//                                         );
//                                     }

//                                     // Example: prevent contextual use (username/app name)
//                                     if (value.toLowerCase().includes('ccost') || value.toLowerCase().includes('cgcost') || value.toLowerCase().includes('admin')
//                                     || value.toLowerCase().includes('user') || value.toLowerCase().includes('nodal') || value.toLowerCase().includes('raipur')) {
//                                         return Promise.reject(
//                                             new Error('Password cannot contain application-specific word, username  or app name.!')
//                                         );
//                                     }

//                                     return Promise.resolve();
//                                 },
//                             },
//                         ]}
//                     >
//                         <Input.Password autoComplete="off" />
//                     </Form.Item>
//                 </Col>

//                 <Col md={12}>
//                     <Form.Item
//                         label="Confirm Password"
//                         name="confirmPassword"
//                         dependencies={['newPassword']}
//                         rules={[
//                             { required: true, message: 'Please confirm your new password!' },
//                             ({ getFieldValue }) => ({
//                                 validator(_, value) {
//                                     if (!value || getFieldValue('newPassword') === value) {
//                                         return Promise.resolve();
//                                     }
//                                     return Promise.reject(new Error('New Password & Confirm Password do not match!'));
//                                 },
//                             }),
//                         ]}
//                     >
//                         <Input.Password autoComplete="off" />
//                     </Form.Item>
//                 </Col>

//                 <Col md={12}>
//                     <hr />
//                 </Col>

//                 <Col md={12} className="text-center">
//                     <button
//                         type="submit"
//                         className="btn btn-sm btn-primary me-2"
//                         disabled={loadingModalSubmit}
//                     >
//                         Submit
//                     </button>
//                     <button
//                         className="btn btn-sm btn-outline-danger"
//                         disabled={loadingModalSubmit}
//                         onClick={modalClose}
//                     >
//                         Cancel
//                     </button>
//                 </Col>
//             </Card.Body>
//         </Card>
//     </Form>
// </Modal>


//                     </Col>
//                 </Row>
//             </Container>
//         </>
//     );
// };

// export default ChangePassword;
